import { createTenantSchema } from '@smarton-pbx/shared';
import { ChevronLeft } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { api } from '../api';
import { Field, Notice, PageHeader } from '../components/ui';
import { TimeZoneSelect } from '../components/pickers';
import { t } from '../i18n';
import { useSubmit } from '../lib/hooks';

const HOSTNAME_HINT = t(
  'The address its people open. On a server: a name whose DNS points here, e.g. phone.customer.com. On this computer: localhost, or a name ending in .localhost.',
);

export function NewTenant() {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [hostname, setHostname] = useState('');
  const [timeZone, setTimeZone] = useState('Europe/Vilnius');
  const [productName, setProductName] = useState('');
  const [primaryColor, setPrimaryColor] = useState('#2F5BD3');
  const [accentColor, setAccentColor] = useState('#1C8A5B');
  const [maxConcurrentCalls, setMaxConcurrentCalls] = useState('20');
  const [dailyMinutesCap, setDailyMinutesCap] = useState('5000');
  const form = useSubmit();

  async function submit(event: FormEvent) {
    event.preventDefault();
    const input = {
      name,
      hostname,
      timeZone,
      branding: { productName, primaryColor, accentColor, emailSenderName: productName },
      maxConcurrentCalls: Number(maxConcurrentCalls),
      dailyMinutesCap: Number(dailyMinutesCap),
    };
    const tenant = await form.submit(createTenantSchema, input, api.platform.createTenant);
    if (tenant) await navigate(`/tenants/${tenant.id}`);
  }

  return (
    <div className="stack">
      <Link to="/" className="row muted" style={{ gap: 4 }}>
        <ChevronLeft size={16} aria-hidden /> {t('Tenants')}
      </Link>
      <PageHeader title={t('New tenant')} description={t('You can change all of this later.')} />
      <form className="card" onSubmit={submit} noValidate>
        <div className="card-header">
          <h2>{t('Company')}</h2>
        </div>
        <div className="card-body form-grid">
          <Field label={t('Company')} hint={t('Shown to platform staff only')} error={form.fields.name}>
            <input value={name} onChange={(e) => setName(e.target.value)} autoFocus data-testid="tenant-name" />
          </Field>
          <Field label={t('Product name')} hint={t('What the company’s people see')} error={form.fields['branding.productName']}>
            <input value={productName} onChange={(e) => setProductName(e.target.value)} data-testid="tenant-product-name" />
          </Field>
          <Field label={t('Hostname')} hint={HOSTNAME_HINT} error={form.fields.hostname}>
            <input value={hostname} onChange={(e) => setHostname(e.target.value)} placeholder="phone.customer.com" data-testid="tenant-hostname" />
          </Field>
          <Field label={t('Time zone')} hint={t('For reports and opening hours')} error={form.fields.timeZone}>
            <TimeZoneSelect value={timeZone} onChange={setTimeZone} />
          </Field>
        </div>
        <div className="card-header" style={{ borderTop: '1px solid var(--border)' }}>
          <h2>{t('Look and limits')}</h2>
        </div>
        <div className="card-body form-grid">
          <Field label={t('Main colour')} error={form.fields['branding.primaryColor']}>
            <span className="color-input">
              <input type="color" value={primaryColor} onChange={(e) => setPrimaryColor(e.target.value)} aria-label={t('Pick the main colour')} />
              <input value={primaryColor} onChange={(e) => setPrimaryColor(e.target.value)} aria-label={t('Main colour')} />
            </span>
          </Field>
          <Field label={t('Accent colour')} error={form.fields['branding.accentColor']}>
            <span className="color-input">
              <input type="color" value={accentColor} onChange={(e) => setAccentColor(e.target.value)} aria-label={t('Pick the accent colour')} />
              <input value={accentColor} onChange={(e) => setAccentColor(e.target.value)} aria-label={t('Accent colour')} />
            </span>
          </Field>
          <Field label={t('Simultaneous calls')} hint={t('Across the whole company')} error={form.fields.maxConcurrentCalls}>
            <input inputMode="numeric" value={maxConcurrentCalls} onChange={(e) => setMaxConcurrentCalls(e.target.value)} />
          </Field>
          <Field label={t('Daily minutes cap')} hint={t('Calls stop for the day after this')} error={form.fields.dailyMinutesCap}>
            <input inputMode="numeric" value={dailyMinutesCap} onChange={(e) => setDailyMinutesCap(e.target.value)} />
          </Field>
        </div>
        {form.error && (
          <div className="card-body" style={{ paddingTop: 0 }}>
            <Notice kind="error">{form.error}</Notice>
          </div>
        )}
        <div className="card-footer">
          <Link className="button secondary" to="/">
            {t('Cancel')}
          </Link>
          <button type="submit" disabled={form.busy} data-testid="create-tenant">
            {t('Create tenant')}
          </button>
        </div>
      </form>
    </div>
  );
}
